import { useContext, useState, useEffect } from "react";
import ScrollToTop from "../../components/ScrollToTop";
import { useParams } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import { Pagination, Autoplay, EffectFade } from "swiper";
import FSLightbox from "fslightbox-react";
import { Helmet } from "react-helmet";
import { HouseContext } from "../../context/HouseContext";
import { BounceLoader } from "react-spinners";
import Map from "../../components/Map";

const PropertyDetails = () => {
  const { houses } = useContext(HouseContext);
  const { id } = useParams();
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightboxToggler, setLightboxToggler] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // Estado para manejar si la propiedad está cargada
  const [property, setProperty] = useState(null);

  useEffect(() => {
    const foundProperty = houses.find((house) => house.id === id);
    setProperty(foundProperty); // Establece la propiedad encontrada
  }, [id, houses]);

  if (!property) {
    return (
      <div className="flex flex-col justify-center items-center h-[90vh]">
        <BounceLoader className="my-2" color="#187795" size={60} />
        <h2 className="text-xl">Cargando Propiedad</h2>
      </div>
    );
  }

  // Función para abrir el lightbox al hacer clic en una imagen
  const openLightbox = (index) => {
    setPhotoIndex(index + 1); // Usar el índice directamente sin sumar 1
    setLightboxToggler(!lightboxToggler); // Alterna el estado del lightbox
  };

  // Función para manejar los cambios en los inputs del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Función para verificar si todos los campos del formulario están completos
  const isFormComplete = () => {
    return (
      formData.name && formData.email && formData.phone && formData.message
    );
  };

  // Función para manejar el envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();

    const whatsappMessage = encodeURIComponent(`\
      *Consulta sobre la propiedad* \n
      *Nombre*: ${formData.name} \n
      *Email*: ${formData.email} \n
      *Teléfono*: ${formData.phone} \n
      *Mensaje*: ${formData.message} \n\n
      *Detalles de la propiedad* \n
      *Nombre*: ${property.name} \n
      *Dirección*: ${property.address} \n
      *Localidad*: ${property.country} \n
      *Tipo*: ${property.type} \n
      *Precio*: ${property.currency} ${
      property.price !== "" ? property.price : "Consultar"
    } \n
      *Descripción*: ${property.description}
    `);

    const whatsappLink = `https://wa.me/+5492241671029?text=${whatsappMessage}`;

    // Abre el enlace de WhatsApp en una nueva pestaña
    window.open(whatsappLink, "_blank");
  };

  return (
    <div className="container mx-auto min-h-[800px] mt-32 mb-14">
      <Helmet>
        <title>{property.name}</title>
        <meta
          name="description"
          content={`Consulta sobre ${property.name}. ${property.description}`}
        />
        <meta
          name="keywords"
          content={`${property.name}, ${property.type}, ${property.country}, propiedades, alquiler`}
        />
      </Helmet>
      <ScrollToTop />
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between container">
        <div>
          <h2 className="text-2xl font-semibold capitalize">{property.name}</h2>
          <h3 className="text-lg mb-4">{property.address}</h3>
        </div>
        <div className="flex flex-col text-end py-1">
          <p className="text-lg"> {property.state}</p>
          <p className="text-3xl font-semibold text-black">
            {property.currency !== "" ? (
              <>
                {property.currency} {property.price}
              </>
            ) : (
              "Consultar"
            )}
          </p>
        </div>
      </div>
      <div className="relative w-full">
        {property.images.length < 5 ? (
          <div className="relative">
            <Swiper
              style={{
                "--swiper-pagination-color": "#ffffff",
                "--swiper-pagination-bullet-inactive-color": "#ffffff",
                "--swiper-pagination-bullet-inactive-opacity": "0.5",
              }}
              loop={property.images.length > 1}
              spaceBetween={0}
              pagination={{ clickable: true, dynamicBullets: true }}
              modules={[Pagination, Autoplay, EffectFade]}
              className="rounded-2xl overflow-hidden shadow-md"
              effect="fade"
              autoplay={
                property.images.length > 1
                  ? { delay: 4000, disableOnInteraction: false }
                  : false
              }
              slidesPerView={1}
              grabCursor={true}
            >
              {property.images.map((image, index) => (
                <SwiperSlide key={index}>
                  <img
                    src={image}
                    alt={`Foto ${index + 1}`}
                    className="w-full object-cover h-[300px] sm:h-[500px] lg:h-[550px] cursor-zoom-in"
                    onClick={() => openLightbox(index)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="absolute bottom-4 right-4 z-10 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <circle cx="12" cy="13" r="3" strokeWidth="2" />
              </svg>
              {property.images.length}{" "}
              {property.images.length === 1 ? "foto" : "fotos"}
            </div>
          </div>
        ) : (
          <>
            {/* Mobile / Tablet: Swiper */}
            <div className="block lg:hidden relative">
              <Swiper
                style={{
                  "--swiper-pagination-color": "#ffffff",
                  "--swiper-pagination-bullet-inactive-color": "#ffffff",
                  "--swiper-pagination-bullet-inactive-opacity": "0.5",
                }}
                loop={true}
                spaceBetween={0}
                pagination={{ clickable: true, dynamicBullets: true }}
                modules={[Pagination, Autoplay, EffectFade]}
                className="rounded-2xl overflow-hidden shadow-md"
                effect="fade"
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                slidesPerView={1}
                grabCursor={true}
              >
                {property.images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <img
                      src={image}
                      alt={`Foto ${index + 1}`}
                      className="w-full object-cover h-[300px] sm:h-[500px] cursor-zoom-in"
                      onClick={() => openLightbox(index)}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="absolute bottom-4 right-4 z-10 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <circle cx="12" cy="13" r="3" strokeWidth="2" />
                </svg>
                {property.images.length} fotos
              </div>
            </div>

            {/* Desktop: grilla hero */}
            <div className="hidden lg:grid grid-cols-4 grid-rows-2 gap-2 h-[550px] rounded-2xl overflow-hidden shadow-md">
              <div
                className="col-span-2 row-span-2 relative group cursor-zoom-in overflow-hidden"
                onClick={() => openLightbox(0)}
              >
                <img
                  src={property.images[0]}
                  alt="Foto principal"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
              </div>

              {property.images.slice(1, 5).map((image, index) => (
                <div
                  key={index}
                  className="relative group cursor-zoom-in overflow-hidden"
                  onClick={() => openLightbox(index + 1)}
                >
                  <img
                    src={image}
                    alt={`Foto ${index + 2}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />

                  {index === 3 && property.images.length > 5 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[2px] group-hover:bg-black/60 transition-colors">
                      <span className="text-white text-3xl font-semibold">
                        +{property.images.length - 5}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => openLightbox(0)}
              className="hidden lg:flex absolute bottom-5 right-5 items-center gap-2 bg-white/95 hover:bg-white transition-all hover:scale-105 text-gray-900 px-4 py-2.5 rounded-lg shadow-lg font-medium text-sm border border-gray-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6a2 2 0 012-2h2l2-2h4l2 2h2a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V6z"
                />
                <circle cx="12" cy="11" r="3" strokeWidth="2" />
              </svg>
              Ver las {property.images.length} fotos
            </button>
          </>
        )}
      </div>
      <FSLightbox
        toggler={lightboxToggler}
        sources={property.images}
        slide={photoIndex}
      />
      <div className="flex items-center justify-center py-8">
        <div className="bg-white w-full">
          <div className="p-4 border-b">
            <h2 className="text-2xl ">Descripción</h2>
          </div>
          <div>
            <div className="hover:bg-gray-50 md:space-y-0 space-y-3 px-4 py-4 text-gray-700 text-sm">
              <p>{property.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <div className="bg-white w-full">
          <div className="p-4 border-b">
            <h2 className="text-2xl">Caracteristicas</h2>
          </div>
          <div className="mt-4">
            <div className="grid grid-cols-2">
              {Object.entries(property.characteristics)
                .filter(([key, value]) => value)
                .map(([key, value], i) => (
                  <div key={i} className="hover:bg-gray-50 p-4 border-b">
                    <p className="text-gray-600 capitalize">{key}</p>
                    <p>{value}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center py-8">
        <div className="bg-white w-full">
          <div className="p-4 border-b">
            <h2 className="text-2xl ">Ubicación</h2>
          </div>
          <div className="mt-4">
            <Map src={property.maps} />
          </div>
        </div>
      </div>
      <div className="flex-1 w-full mb-8 bg-white border border-gray-300 rounded-lg px-6 py-8">
        <h2 className="text-xl font-semibold mb-4">Contacto</h2>
        <form className="flex flex-col gap-y-4" onSubmit={handleSubmit}>
          <input
            className="border border-gray-300 focus:border-primary rounded w-full px-4 h-14 text-sm outline-none"
            type="text"
            name="name"
            placeholder="Nombre*"
            value={formData.name}
            onChange={handleInputChange}
          />
          <input
            className="border border-gray-300 focus:border-primary rounded w-full px-4 h-14 text-sm outline-none"
            type="email"
            name="email"
            placeholder="Email*"
            value={formData.email}
            onChange={handleInputChange}
          />
          <input
            className="border border-gray-300 focus:border-primary rounded w-full px-4 h-14 text-sm outline-none"
            type="text"
            name="phone"
            placeholder="Teléfono*"
            value={formData.phone}
            onChange={handleInputChange}
          />
          <textarea
            className="border border-gray-300 focus:border-primary rounded w-full p-4 h-36 text-sm text-gray-400 outline-none resize-none"
            name="message"
            placeholder="Mensaje*"
            value={formData.message}
            onChange={handleInputChange}
          />
          <div className="flex gap-x-2">
            <button
              className={`${
                isFormComplete()
                  ? "bg-primary hover:opacity-75"
                  : "bg-gray-400 cursor-not-allowed"
              } text-white rounded p-4 text-sm w-full transition flex justify-center items-center`}
              type="submit"
              disabled={!isFormComplete()} // Deshabilitar el botón si el formulario no está completo
            >
              <FaWhatsapp className="text-2xl mr-2" />
              Enviar mensaje
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyDetails;
